
import React, { useState } from 'react';
import { Users, FileText, Download, RefreshCw, Target, Users2, Radio, Lightbulb, Cog, Palette, BarChart3 } from 'lucide-react';
import ReportViewer from './ReportViewer';
import ExportDataDialog from './ExportDataDialog';
import SurveyDownloadDialog from './SurveyDownloadDialog';
import { CampaignFormData, WorkflowStep } from '../types';
import { toast } from 'react-hot-toast';

interface WorkflowSummaryProps {
  formData: CampaignFormData;
  steps: WorkflowStep[];
  consolidatedReport: string | null;
  selectedReport: { title: string; content: string } | null;
  isGeneratingConsolidated: boolean;
  onViewResult: (stepId: number) => void;
  onGenerateConsolidatedReport: () => void;
  onDownloadAllReports: () => void;
  onRestartAnalysis: () => void;
  onReset: () => void;
  onCloseReport: () => void;
  onShowConsolidatedReport: () => void;
}

const WorkflowSummary: React.FC<WorkflowSummaryProps> = ({
  formData,
  steps,
  consolidatedReport,
  selectedReport,
  isGeneratingConsolidated,
  onViewResult,
  onGenerateConsolidatedReport,
  onDownloadAllReports,
  onRestartAnalysis,
  onReset,
  onCloseReport,
  onShowConsolidatedReport
}) => {
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showSurveyDialog, setShowSurveyDialog] = useState(false);

  const handleExportCurrentData = () => {
    // Check if there's meaningful data to export
    const hasData = formData.core_issue_details.campaign_name || 
                   formData.core_issue_details.political_issue ||
                   formData.campaign_outcome.winning_aspiration;
    
    if (!hasData) {
      toast.error('No campaign data to export. Please fill in some form fields first.');
      return;
    }
    
    setShowExportDialog(true);
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'aspiration':
        return Target;
      case 'where-to-play':
        return Users2;
      case 'how-to-win':
        return Lightbulb;
      case 'capabilities':
        return Cog;
      case 'management':
        return Radio;
      default:
        return FileText;
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Users className="h-6 w-6 mr-2 text-primary-600" />
            <h2 className="text-xl font-semibold text-primary-800">Political Campaign Analysis Complete</h2>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={onDownloadAllReports}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center"
              disabled={isGeneratingConsolidated}
            >
              <Download className="h-4 w-4 mr-2" />
              Download All Reports
            </button>
            <button
              onClick={handleExportCurrentData}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Campaign Data
            </button>
            {!consolidatedReport && (
              <button
                onClick={onGenerateConsolidatedReport}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                disabled={isGeneratingConsolidated}
              >
                {isGeneratingConsolidated ? 'Generating...' : 'Generate Consolidated Report'}
              </button>
            )}
            <button
              onClick={onRestartAnalysis}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Re-run Analysis
            </button>
            <button
              onClick={onReset}
              className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-md hover:bg-neutral-200 transition-colors"
            >
              Start New Campaign
            </button>
          </div>
        </div>

        {/* Professional Formatting Notice */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <Palette className="h-5 w-5 text-purple-600 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-purple-800">Professional Report Formatting Available</h3>
              <p className="text-sm text-purple-700">Click on any report below to access professional templates, branding options, and multi-format export (PDF, DOCX, PowerPoint)</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <h3 className="font-medium text-neutral-700">Campaign Name</h3>
            <p className="text-neutral-900">{formData.core_issue_details.campaign_name}</p>
          </div>
          <div>
            <h3 className="font-medium text-neutral-700">Political Issue</h3>
            <p className="text-neutral-900">{formData.core_issue_details.political_issue}</p>
          </div>
          <div>
            <h3 className="font-medium text-neutral-700">Target Entity</h3>
            <p className="text-neutral-900">{formData.core_issue_details.target_entity}</p>
          </div>
          <div>
            <h3 className="font-medium text-neutral-700">Winning Aspiration</h3>
            <p className="text-neutral-900">{formData.campaign_outcome.winning_aspiration}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {steps.map((step) => {
          const StageIcon = getStageIcon(step.stage);
          return (
            <button
              key={step.id}
              onClick={() => onViewResult(step.id)}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow text-left"
            >
              <div className="flex items-start mb-3">
                <StageIcon className="h-5 w-5 mr-2 text-primary-600 mt-1" />
                <h3 className="text-lg font-medium text-primary-800">{step.title}</h3>
              </div>
              <p className="text-sm text-neutral-600">{step.description}</p>
              <div className="mt-4 text-primary-600 text-sm font-medium">
                Click to view report →
              </div>
            </button>
          );
        })}

        {consolidatedReport && (
          <button
            onClick={onShowConsolidatedReport}
            className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow text-left"
          >
            <div className="flex items-start mb-3">
              <FileText className="h-5 w-5 mr-2 text-primary-600 mt-1" />
              <h3 className="text-lg font-medium text-primary-800">Consolidated Report</h3>
            </div>
            <p className="text-sm text-neutral-600">Complete analysis with executive summary and campaign recommendations</p>
            <div className="mt-4 text-primary-600 text-sm font-medium">
              Click to view report →
            </div>
          </button>
        )}
      </div>

      {/* Survey Creation Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <BarChart3 className="h-8 w-8 text-blue-600 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">Create Public Opinion Survey</h3>
            <p className="text-sm text-blue-700 mb-4">
              Use your campaign research to create targeted surveys and polls. The poll assistant can help you 
              design effective questions based on your campaign analysis and political context.
            </p>
            <button
              onClick={() => setShowSurveyDialog(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium flex items-center"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Create Survey
            </button>
          </div>
        </div>
      </div>

      {selectedReport && (
        <ReportViewer
          title={selectedReport.title}
          content={selectedReport.content}
          onClose={onCloseReport}
        />
      )}

      <ExportDataDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        formData={formData}
      />

      <SurveyDownloadDialog
        isOpen={showSurveyDialog}
        onClose={() => setShowSurveyDialog(false)}
        steps={steps}
        consolidatedReport={consolidatedReport}
        campaignName={formData.core_issue_details.campaign_name}
      />
    </div>
  );
};

export default WorkflowSummary;
